import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
}

function useUsers() {
  return useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') });
}

export function UsersListPage() {
  const { data: users, isLoading, error } = useUsers();
  const qc = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete user';
      toast.error(msg);
    },
    onSettled: () => setDeletingId(null),
  });

  const handleDelete = (user: User) => {
    if (!confirm(`Delete ${user.fullName}? This cannot be undone.`)) return;
    setDeletingId(user.id);
    deleteMutation.mutate(user.id);
  };

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage CRM user accounts and permissions"
        action={
          <Button asChild>
            <Link to="/settings/users/new">
              <Plus className="h-4 w-4" />
              New user
            </Link>
          </Button>
        }
      />

      <div className="p-6">
        {isLoading && (
          <div className="flex justify-center py-12">
            <Spinner className="h-6 w-6" />
          </div>
        )}

        {error && (
          <p className="text-sm text-danger-700">Failed to load users.</p>
        )}

        {users && (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Role</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Last login</th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-neutral-500">
                      No users found.
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-tangerine-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-neutral-900">{user.fullName}</td>
                    <td className="px-4 py-3 text-neutral-700">{user.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={user.role as 'super_admin' | 'admin'}>
                        {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={user.status as 'active' | 'disabled'}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-neutral-500">{formatDate(user.lastLoginAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" asChild title="Edit user">
                          <Link to={`/settings/users/${user.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild title="Permissions">
                          <Link to={`/settings/users/${user.id}/permissions`}>
                            <KeyRound className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Delete user"
                          disabled={deletingId === user.id}
                          onClick={() => handleDelete(user)}
                          className="text-danger-700 hover:bg-danger-50"
                        >
                          {deletingId === user.id
                            ? <Spinner className="h-4 w-4" />
                            : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
